import {
  FileSummary,
  IDocUnitLesson,
  IEducationalAlignment,
  IEducationalAlignmentLevel,
  IEducationalAlignmentTeaches,
} from './types'
import { NodeType } from 'shared/types/ingestion'
import { INoteNode, ISkillsList } from '../../components/ingestion/IngestedTree'
import { createNoteNode } from '../nodeCreationHelpers'
import { PARAGRAPH_SPACING, wrapB, wrapP } from '../html_utility'
import { OSEStudentFacingSections } from '../Quantiphi/OpenSciEd/types'
import { oseIconAlts } from './constants'
import { parseHtmlString } from './html'
import { makeGetRequest } from 'infrastructure/api'
import { GDriveUnitFiles } from './atoms'

export function getGradeCode(
  alignments: IEducationalAlignment | IEducationalAlignment[],
) {
  if (!Array.isArray(alignments)) {
    alignments = [alignments]
  }
  const alignment = alignments.find(
    (a) => a.alignmentType === 'Educational level',
  ) as IEducationalAlignmentLevel
  if (!alignment) throw new Error('Grade not found')
  return parseInt(alignment.targetName) + 1
}

export function skillIdsFromAlignments(
  alignments: IEducationalAlignment | IEducationalAlignment[],
) {
  if (!Array.isArray(alignments)) {
    alignments = [alignments]
  }
  const filtered = alignments.filter(
    (a) => a.alignmentType === 'teaches',
  ) as IEducationalAlignmentTeaches[]
  const ids = filtered.map((a: IEducationalAlignmentTeaches) => {
    if (typeof a.targetName === 'string') return a.targetName
    return a.targetName.name
  })
  return Array.from(new Set(ids))
}

export function createSkillsListForIds(skillIds: string[]): ISkillsList | null {
  if (!skillIds.length) return null
  return {
    type: NodeType.SkillsList,
    data: {
      title: 'Building towards NGSS Performance Expectations',
      skills: skillIds as [string, ...string[]],
    },
  }
}

export function skillsListFromAlignments(
  alignments: IEducationalAlignment | IEducationalAlignment[],
) {
  const skillIds = skillIdsFromAlignments(alignments)
  return createSkillsListForIds(skillIds)
}

export function performanceExpectations(
  alignments: IEducationalAlignment[],
): string {
  if (!Array.isArray(alignments)) {
    alignments = [alignments]
  }
  const title = wrapP(wrapB('Building Toward NGSS Performance Expectations'))
  const filtered = alignments.filter(
    (a) => a.alignmentType === 'teaches',
  ) as IEducationalAlignmentTeaches[]
  return (
    title +
    PARAGRAPH_SPACING +
    filtered
      .map((a) => {
        if (typeof a.targetName === 'string') {
          return wrapB(a.targetName)
        } else {
          return (
            wrapP(wrapB(`${a.targetName.name}:`)) +
            wrapP(a.targetName.description)
          )
        }
      })
      .join('')
  )
}

export function lessonDescriptions({
  descriptionPast,
  descriptionPresent,
}: {
  descriptionPast?: string
  descriptionPresent: string
}) {
  const noteNodes: INoteNode[] = []

  if (descriptionPast) {
    noteNodes.push(
      createNoteNode({
        title: 'Previous Lesson',
        value: descriptionPast,
        studentFacing: false,
      }),
    )
  }

  noteNodes.push(
    createNoteNode({
      title: 'This Lesson',
      value: descriptionPresent,
      studentFacing: false,
    }),
  )

  return noteNodes
}

export function styleContinuousLines(element: HTMLElement | Document) {
  const continuousLines = Array.from(
    element.getElementsByTagName?.('span') || [],
  ).filter((s) => {
    return s.textContent && /^_+$/.test(s.textContent)
  })
  continuousLines.forEach((c) => {
    c.style.letterSpacing = '-0.5em'
    c.style.marginRight = '0.5em'
  })
}

export function styleTables(element: HTMLElement | Document) {
  const tables = element.querySelectorAll('td')
  Array.from(tables || []).forEach((t) => {
    t.style.verticalAlign = 'top'
  })
}

export function removeFigures(element: HTMLElement | Document) {
  // Select all figure elements that contain an image
  const figures = element.querySelectorAll('figure img')
  const filteredImages = Array.from(figures).filter(
    (img) => !oseIconAlts.includes((img as HTMLImageElement).alt),
  )

  filteredImages.forEach((elem) => {
    const img = elem as HTMLImageElement
    // Get the parent figure element of the image
    const figure = img.parentElement

    // Check if the parent is indeed a figure
    if (figure?.tagName === 'FIGURE') {
      img.style.margin = 'auto'
      img.style.display = 'table'
      img.style.width = '25%'

      // Replace the figure with the image by inserting the image before the figure
      // and then removing the figure element
      figure.parentNode?.insertBefore(img, figure)
      figure.parentNode?.removeChild(figure)
    }
  })
}

function styleIcons(element: HTMLElement) {
  const icons = element.getElementsByClassName?.('o-ose-icon--without-text')
  Array.from(icons || []).forEach((i) => {
    i.removeAttribute('onerror')
    ;(i as HTMLElement).style.width = '30px'
  })
}

function styleThumbnails(element: HTMLElement) {
  const thumbnails = element.getElementsByClassName?.(
    'o-ose-image--float o-ose-thumbnail',
  )
  Array.from(thumbnails || []).forEach((t) => {
    t.removeAttribute('onerror')
    ;(t as HTMLElement).style.float = 'right'
  })
}

function styleRows(element: HTMLElement) {
  if (element.className?.includes('row')) {
    element.style.display = 'flex'
    element.childNodes.forEach((c) => {
      element.style.padding = '5px'
    })
  }
}

export function fixBuildingTowardsBold(element: HTMLElement | Document) {
  const buildingTowards = Array.from(
    element.querySelectorAll('*') || [],
  ).filter((e) => e.textContent?.trim() === 'Building towards')
  buildingTowards.forEach((bt) => {
    ;(bt as HTMLElement).style.fontWeight = '700'
  })
}

export function removeUnneededElements(doc: Document) {
  doc.body.innerHTML = doc.body.innerHTML.replace(/--GDOC-PAGE-BREAK--/g, '')
  doc.body.querySelectorAll('.u-ose-remove-me').forEach((e) => e.remove())
  // doc.body.innerHTML = doc.body.innerHTML.replace(/<p class="u-ose-remove-me"><\/p>/g, '')
  doc.body.innerHTML = doc.body.innerHTML.replace(
    /\[icon-assessment\]/g,
    '<img class="o-ose-icon--without-text" alt="assessment" width="30" src="https://kiddom-static.s3.amazonaws.com/icons/tx/assessment_icon.png">',
  )
}

export function removeFontStyles(element: HTMLElement | Document) {
  var allElements = element.querySelectorAll('*')

  // Iterate over each element
  allElements.forEach(function (element) {
    if (element instanceof HTMLElement) {
      if (element.style.fontSize) {
        element.style.fontSize = ''
      }
      if (element.style.fontFamily) {
        element.style.fontFamily = ''
      }
      if (element.style.backgroundColor) {
        element.style.backgroundColor = ''
      }
      if (element.style.borderBottomColor) {
        element.style.borderBottomColor = ''
      }
      if (element.style.borderRightColor) {
        element.style.borderRightColor = ''
      }
      if (element.style.borderLeftColor) {
        element.style.borderLeftColor = ''
      }
      if (element.style.borderTopColor) {
        element.style.borderTopColor = ''
      }
    }
  })
}

const titleStyles = [
  ['ALTERNATE ACTIVITY', 'color: #3a5692'],
  ['ASSESSMENT OPPORTUNITY', 'color: #22aa4a'],
  ['HOME LEARNING OPPORTUNITY', 'color: #8e00c2'],
]

// This builds an html string from children, any time a table is found we
// take the first cell and make it into a header, including an image if any
// The contents of the second sell are appeneded as is
export function flattenChildTablesWhereFirstCellIsHeader(
  element: HTMLElement,
): string {
  let contentItems: string[] = []
  const children = Array.from(element.childNodes) as HTMLElement[]
  const h1Index = children.findIndex((c) => c.tagName === 'H1')
  children
    .slice(0, h1Index < 0 ? undefined : h1Index)
    .forEach((childAsHTMLElement, index) => {
      styleRows(childAsHTMLElement)
      styleIcons(childAsHTMLElement)
      styleThumbnails(childAsHTMLElement)
      styleContinuousLines(childAsHTMLElement)

      if (
        childAsHTMLElement.tagName === 'TABLE' &&
        childAsHTMLElement.className.includes('o-ose-callout')
      ) {
        Array.from((childAsHTMLElement as HTMLTableElement).rows).forEach(
          (subRow) => {
            let newDiv = '<div>'

            const headerCell = subRow.cells.item(0)
            let studentFacing = false
            if (headerCell?.textContent) {
              if (
                Object.values(OSEStudentFacingSections).some((term) =>
                  headerCell.textContent?.toLowerCase().includes(term),
                )
              ) {
                studentFacing = true
              }

              const style = titleStyles.find(([title]) =>
                headerCell.textContent?.includes(title),
              )?.[1]
              const attrs = style ? { style } : undefined
              newDiv += wrapP(wrapB(headerCell.textContent), attrs)
            }
            const img = headerCell?.getElementsByTagName('img').item(0)
            img?.setAttribute('width', '85')
            if (studentFacing) {
              newDiv += '<div class="student-facing-grouped">'
            }
            newDiv += img?.outerHTML || ''
            newDiv +=
              subRow.cells
                .item(1)
                ?.outerHTML?.replace(/<p><\/p>/g, PARAGRAPH_SPACING) || ''

            if (studentFacing) {
              newDiv += '</div>'
            }
            newDiv += '</div>'
            contentItems.push(newDiv)
          },
        )
      } else {
        const content =
          childAsHTMLElement.outerHTML?.trim() ||
          childAsHTMLElement.textContent?.trim()
        content && content !== '<p></p>' && contentItems.push(content)
      }
    })
  return contentItems
    .filter((s) => !!s.trim())
    .join(PARAGRAPH_SPACING)
    .replaceAll('<p></p>', PARAGRAPH_SPACING)
}

export function getLessonNum(metadata: IDocUnitLesson) {
  return metadata['@id'].split('-LS')[1].split('-L')[1]
}

export function getLessonElement(metadata: IDocUnitLesson, doc: Document) {
  const itemId = `Lesson_${metadata['@id']}`
  const lessonElement = doc.getElementById(itemId)
  if (!lessonElement) throw new Error('Getting Lesson HTML Element')
  return lessonElement
}

export type FilesByUnitType = Record<
  string,
  Record<
    string,
    {
      name: string
      isFile: boolean
      key: string
      URL: string
      last_modified: string
    }
  >
>

export function sortUnitFiles(unitFiles: any[]) {
  return unitFiles.reduce<FilesByUnitType>((acc, file) => {
    if (!file.isFile) return acc
    const [gradePart, unitSection] = file.name.split('.')
    const [unitPart] = unitSection.split('-')
    if (!gradePart || !unitPart) return acc
    if (!acc[gradePart]) {
      acc[gradePart] = {}
    }
    return {
      ...acc,
      [gradePart]: {
        ...acc[gradePart],
        [unitPart]: file,
      },
    }
  }, {})
}

export async function htmlDocForUnitUrl(url: string) {
  const { result: unitData } = await makeGetRequest<string>({ url })
  if (!unitData) return
  return parseHtmlString(unitData)
}

export function courseFilesSummary(
  courseFiles: { fileMap: GDriveUnitFiles | undefined; courseKey: string }[],
): FileSummary[] {
  return courseFiles.map((fileGroup) => {
    const { courseKey, fileMap } = fileGroup
    if (!fileMap || !Object.keys(fileMap).length) {
      return {
        courseKey: String(courseKey),
      }
    }
    const { lessons, extras } = Object.keys(fileMap).reduce<{
      lessons: number[]
      extras: string[]
    }>(
      (acc, key) => {
        if (key.startsWith('L')) {
          acc.lessons.push(parseInt(key.slice(1), 10))
          return acc
        }
        acc.extras.push(key)
        return acc
      },
      { lessons: [], extras: [] },
    )
    const minLesson = Math.min(...lessons)
    const maxLesson = Math.max(...lessons)
    const missing: number[] = []
    for (let n = minLesson + 1; n < maxLesson; n++) {
      if (!lessons.includes(n)) {
        missing.push(n)
      }
    }
    return {
      courseKey: String(courseKey),
      lessons: [minLesson, maxLesson, ...missing],
      hasAsmt: extras.includes('ASMT'),
      hasTbk: extras.includes('TBK'),
    }
  })
}
